import { MfaFactor } from '@logto/schemas';
import { trySafe } from '@silverhand/essentials';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import {
  createForgotPasswordPasskeyAuthentication,
  verifyForgotPasswordPasskey,
} from '@/apis/experience';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';

import useApi from './use-api';
import useErrorHandler from './use-error-handler';
import useToast from './use-toast';

const isUserCancellation = (error: unknown) =>
  error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError');

type PreparedAuthentication = {
  options?: Awaited<ReturnType<typeof createForgotPasswordPasskeyAuthentication>>['options'];
  error?: unknown;
};

/**
 * Complete a passkey assertion for password recovery and leave the interaction
 * on the set-new-password step. The interaction is not submitted as a sign-in.
 */
const useForgotPasswordPasskey = (isEnabled: boolean) => {
  const [authenticationOptions, setAuthenticationOptions] =
    useState<Awaited<ReturnType<typeof createForgotPasswordPasskeyAuthentication>>['options']>();
  const [hasCompleted, setHasCompleted] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const preparationRef = useRef<Promise<PreparedAuthentication>>();
  const { t } = useTranslation();
  const { setToast } = useToast();
  const handleError = useErrorHandler();
  const navigate = useNavigateWithPreservedSearchParams();
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);
  const asyncCreateAuthentication = useApi(createForgotPasswordPasskeyAuthentication, {
    silent: true,
  });
  const asyncVerifyAuthentication = useApi(verifyForgotPasswordPasskey);

  const prepare = useCallback(async (): Promise<PreparedAuthentication> => {
    if (!isEnabled) {
      return {};
    }

    if (authenticationOptions) {
      return { options: authenticationOptions };
    }

    const existingPreparation = preparationRef.current;
    if (existingPreparation) {
      return existingPreparation;
    }

    setIsPreparing(true);
    const preparation = (async (): Promise<PreparedAuthentication> => {
      const [error, authentication] = await asyncCreateAuthentication();

      if (error || !authentication) {
        return { error };
      }

      const { options } = authentication;
      setAuthenticationOptions(options);
      return { options };
    })();
    // eslint-disable-next-line @silverhand/fp/no-mutation
    preparationRef.current = preparation;

    try {
      return await preparation;
    } finally {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      preparationRef.current = undefined;
      setIsPreparing(false);
    }
  }, [asyncCreateAuthentication, authenticationOptions, isEnabled]);

  // Safari is stricter than Chromium about transient user activation for
  // WebAuthn. Prepare the server-side challenge before the user clicks so the
  // click handler can call `navigator.credentials.get()` immediately.
  useEffect(() => {
    if (isEnabled && !isProcessing && !hasCompleted) {
      void prepare();
    }
  }, [hasCompleted, isEnabled, isProcessing, prepare]);

  const authenticate = useCallback(async () => {
    if (!isEnabled || isProcessing || hasCompleted) {
      return;
    }

    if (!browserSupportsWebAuthn()) {
      setToast(t('mfa.webauthn_not_supported'));
      return;
    }

    setIsProcessing(true);
    try {
      const prepared = authenticationOptions ? { options: authenticationOptions } : await prepare();

      if (prepared.error) {
        await handleError(prepared.error);
        return;
      }

      const { options } = prepared;
      if (!options) {
        return;
      }

      try {
        WebAuthnAbortService.cancelCeremony();
      } catch {
        // Best-effort cleanup of conditional WebAuthn UI before a user-driven request.
      }

      const response = await trySafe(
        async () => startAuthentication(options),
        (error: unknown) => {
          if (!isUserCancellation(error)) {
            setToast(t('mfa.webauthn_failed_to_verify'));
          }
        }
      );

      if (!response) {
        setAuthenticationOptions(undefined);
        return;
      }

      const [verifyError] = await asyncVerifyAuthentication({
        ...response,
        type: MfaFactor.WebAuthn,
      });

      if (verifyError) {
        // Never reuse an assertion challenge after a failed verify. Clear it
        // only after the request finishes; clearing it earlier would let the
        // preload effect replace the interaction challenge mid-verification.
        setAuthenticationOptions(undefined);
        await handleError(verifyError);
        return;
      }

      setHasCompleted(true);
      setForgotPasswordIdentifierInputValue(undefined);
      navigate('/forgot-password/reset');
    } finally {
      setIsProcessing(false);
    }
  }, [
    asyncVerifyAuthentication,
    authenticationOptions,
    hasCompleted,
    handleError,
    isEnabled,
    isProcessing,
    navigate,
    prepare,
    setForgotPasswordIdentifierInputValue,
    setToast,
    t,
  ]);

  return { authenticate, isPreparing, isProcessing, prepare };
};

export default useForgotPasswordPasskey;
